/**
 * REMI at a glance: alarms and checks, queue and latency, followers, the leader and its
 * database, and REMI's logs, all from CloudWatch (backend remi_dashboard.py). Every tile,
 * alarm and log line opens a side panel with its detail and timeline.
 *
 * Colour means one thing everywhere on this page: green is within limits, orange is a
 * warning that may become red, red needs attention now. Chart series never use them.
 * Where a measure has percentiles, p50 and p95 are shown side by side at the same size.
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { ColumnChart, type Series as ColumnSeries } from '@/components/ColumnChart'
import { Icon } from '@/components/Icon'
import { Legend, LineChart, type Line } from '@/components/LineChart'
import { SidePanel } from '@/components/SidePanel'
import { useBoardData } from '@/components/useBoardData'
import { queryPath } from '@/lib/task-runs'
import './remi.css'

interface Series { id: string; label: string; points: [number, number][] }
interface Alarm { id?: string; name: string; source?: 'remi' | 'agents'; state: 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';
  description: string; since: string; reason: string; severity?: string | null; component?: string | null; recommendation?: string | null }
interface Overview {
  environment: Environment
  window: Window
  period: number
  start: string
  end: string
  generated_at?: string
  simulated?: boolean
  first_step_snapshot?: { start: string; end: string }
  /** The bucket in progress: points from here on are the current bucket so far, after
   *  `end` (the last complete bucket's end). Summaries and comparisons never include it. */
  partial_from?: string | null
  partial_end?: string | null
  summary?: Overview
  current_panels?: Overview['panels']
  previous?: Overview
  panels: Record<string, Series[]>
  free_slots: { points: [number, number][]; current_seconds: number; longest_seconds: number; pools: string[] }
  alarms: Alarm[]
  status: { level: 'ok' | 'alarm' | 'unknown'; firing: string[]; evaluated_at?: string; freshness_seconds?: number;
    checks: { name: string; ok: boolean | null; state?: 'ok' | 'alarm' | 'unknown' | 'stale' | 'not_applicable'; observed_at?: string | null; detail?: string }[] }
  fleet_reporting: boolean
  /** What this REMI reports: SlotsFree and JobsFailed only from seldon-remi#49 on. */
  reports?: { slots_free: boolean }
}
type LiveStatus = Pick<Overview, 'environment' | 'alarms' | 'status' | 'generated_at' | 'simulated'>
type Environment = 'production' | 'staging'
type Window = 'all' | '6h' | '24h' | '7d'
type Tone = 'ok' | 'warn' | 'bad' | 'none'
const WINDOWS: [Window, string][] = [['all', 'All'], ['6h', 'Last 6 hours'], ['24h', 'Last 24 hours'], ['7d', 'Last 7 days']]
// Categorical series colours, in a fixed order. None of them is green, orange or red:
// those are reserved for status, so a chart line never looks like one. p50 is always
// the first, p95 the second.
const C = ['var(--chart-model)', 'var(--remi-blue)', 'var(--remi-magenta)', 'var(--remi-slate)']
const REFRESH_MS = 60_000
const TONE_LABEL: Record<Tone, string> = { ok: 'OK', warn: 'Warning', bad: 'Broken', none: 'No data' }

// --- Formatting ------------------------------------------------------------------------------

const count = (v: number) => Math.round(v).toLocaleString()
const pct = (v: number) => `${v < 10 ? v.toFixed(1) : Math.round(v)}%`
function secs(v: number) {
  if (v < 0.01) return `${(v * 1000).toFixed(1)} ms`
  if (v < 1) return `${Math.round(v * 1000)} ms`
  if (v < 90) return `${v < 10 ? v.toFixed(1) : Math.round(v)} s`
  if (v < 5400) return `${(v / 60).toFixed(v < 600 ? 1 : 0)} min`
  return `${(v / 3600).toFixed(1)} h`
}
const ms = (v: number) => secs(v / 1000)
const gb = (v: number) => `${(v / 1e9).toFixed(1)} GB`
const ZURICH = 'Europe/Zurich'
const time = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { timeZone: ZURICH, hour: '2-digit', minute: '2-digit' })
const day = (t: number) => new Date(t * 1000).toLocaleDateString('en-GB', { timeZone: ZURICH, month: 'short', day: 'numeric' })
const when = (t: number) => new Date(t * 1000).toLocaleString('en-GB', { timeZone: ZURICH, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
const iso = (t: number) => new Date(t * 1000).toISOString()
// Logs Insights returns UTC timestamps without a zone; ISO API times already include one.
const unix = (s: string) => Date.parse(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s.replace(' ', 'T')}Z`) / 1000

// --- Reading series --------------------------------------------------------------------------

function pick(page: Overview, panel: string, id: string) {
  return page.panels[panel]?.find(s => s.id === id)
}
function line(page: Overview, panel: string, id: string, color: string, label?: string, dashed?: boolean): Line {
  const s = pick(page, panel, id)
  return { label: label ?? s?.label ?? id, color, points: s?.points ?? [], dashed }
}
function latest(points?: [number, number][]) {
  return points?.length ? points[points.length - 1][1] : undefined
}
function sum(series: { points: [number, number][] }[]) {
  return series.reduce((a, s) => a + s.points.reduce((b, [, v]) => b + v, 0), 0)
}
function show(v: number | undefined, f: (v: number) => string) { return v == null ? '—' : f(v) }
/** The page without the bucket in progress: headlines, figures and timelines use complete
 *  buckets only, and only charts show the current one so far. */
function complete(page: Overview): Overview {
  if (!page.partial_from) return page
  const cut = unix(page.partial_from)
  const keep = (points: [number, number][]) => points.filter(([t]) => t < cut)
  return { ...page, partial_from: null,
    panels: Object.fromEntries(Object.entries(page.panels).map(([name, series]) => [name, series.map(s => ({ ...s, points: keep(s.points) }))])),
    free_slots: { ...page.free_slots, points: keep(page.free_slots.points) } }
}
/** Several labelled series as stacked columns: the biggest few by name, the rest as Other. */
function stacked(series: Series[], keep = 3): { columns: { start: string; parts: number[] }[]; legend: ColumnSeries[] } {
  const ranked = [...series].sort((a, b) => sum([b]) - sum([a])).filter(s => sum([s]) > 0)
  const named = ranked.slice(0, keep)
  const rest = ranked.slice(keep)
  const legend = [...named.map((s, i) => ({ label: s.label, color: C[i] })), ...(rest.length ? [{ label: 'Other', color: C[3] }] : [])]
  const times = [...new Set(ranked.flatMap(s => s.points.map(([t]) => t)))].sort((a, b) => a - b)
  const at = (s: Series, t: number) => s.points.find(([u]) => u === t)?.[1] ?? 0
  return { legend, columns: times.map(t => ({ start: iso(t), parts: [...named.map(s => at(s, t)), ...(rest.length ? [rest.reduce((a, s) => a + at(s, t), 0)] : [])] })) }
}
/** Combine count samples into readable, equal-duration bars without changing totals. */
function countBuckets(data: ReturnType<typeof stacked>, period: number, maxBars: number) {
  const source = [...data.columns].sort((a, b) => unix(a.start) - unix(b.start))
  if (!source.length) return { ...data, seconds: period, end: 0 }
  const start = unix(source[0].start), end = unix(source[source.length - 1].start) + period
  const minimum = Math.max(period, (end - start) / maxBars)
  const steps = [60, 300, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400, 2 * 86400, 7 * 86400, 14 * 86400, 28 * 86400]
  const seconds = steps.find(step => step >= minimum && step % period === 0) ?? Math.ceil(minimum / period) * period
  const columns = Array.from({ length: Math.ceil((end - start) / seconds) }, (_, i) => ({
    start: iso(start + i * seconds), parts: data.legend.map(() => 0),
  }))
  for (const column of source) {
    const bucket = columns[Math.floor((unix(column.start) - start) / seconds)]
    column.parts.forEach((value, i) => { bucket.parts[i] = (bucket.parts[i] ?? 0) + value })
  }
  return { legend: data.legend, columns, seconds, end }
}
const bucketSize = (seconds: number) => seconds >= 86400 ? `${seconds / 86400} days` : secs(seconds)

const totals = (data: ReturnType<typeof stacked>): [number, number][] =>
  data.columns.map(col => [unix(col.start), col.parts.reduce((a, b) => a + b, 0)])
/** Stretches of time where a series was notable: the raw material of a timeline. */
function episodes(points: [number, number][], notable: (v: number) => boolean, period: number) {
  const out: { from: number; to: number; peak: number }[] = []
  for (const [t, v] of points) {
    const last = out[out.length - 1]
    if (!notable(v)) continue
    if (last && t - last.to <= period * 1.5) { last.to = t; last.peak = Math.max(last.peak, v) }
    else out.push({ from: t, to: t, peak: v })
  }
  return out
}
const worst = (...tones: Tone[]): Tone =>
  tones.includes('bad') ? 'bad' : tones.includes('warn') ? 'warn' : tones.includes('ok') ? 'ok' : 'none'
/** A tone from limits: below warn is OK, from warn orange, from bad red. */
const byLimits = (v: number | undefined, warn: number, bad: number): Tone => v == null ? 'none' : v >= bad ? 'bad' : v >= warn ? 'warn' : 'ok'

// --- What each tile is -----------------------------------------------------------------------

/** p50 and p95 of one measure, shown side by side at the same size. */
interface Percentiles { label?: string; p50?: number; p95?: number; format: (v: number) => string }

interface Spec {
  key: string
  title: string
  sub: string
  /** How it is measured, for the detail panel. */
  definition: string
  /** When it turns orange and red, in words. */
  limits: string
  tone: Tone
  /** The headline, unless the measure is a pair of percentiles. */
  value?: string
  numericValue?: number
  improvement?: 'up' | 'down' | 'neutral'
  unit?: string
  /** The headline as p50 and p95. */
  percentiles?: Percentiles
  cohorts?: { warm?: number; cold?: number }
  /** A second measure's p50 and p95, smaller but equal to each other. */
  secondary?: Percentiles
  detail: string
  chart: (compact: boolean) => ReactNode
  /** Per-series figures in the detail panel. */
  stats: { label: string; points: [number, number][]; format: (v: number) => string }[]
  /** Stretches worth a timeline entry, and how to describe their peak. */
  notable?: { label: string; points: [number, number][]; test: (v: number) => boolean; format: (v: number) => string; tone: Tone }
  alarms: string[]
  logs?: LogKind
  /** A per-endpoint table in the detail panel, over the whole selected window. */
  breakdown?: { title: string; note: string; columns: [string, string, string]; rows: { label: string; values: [string, string] }[]; empty: string }
}

const CURRENT_CARDS = new Set(['utilisation', 'follower-host', 'leader-host', 'db-pool', 'rds'])

function headline(spec: Spec) {
  if (spec.cohorts) return `warm jobs ${show(spec.cohorts.warm, secs)} · cold jobs ${show(spec.cohorts.cold, secs)}`
  if (!spec.percentiles) return `${spec.value ?? '—'}${spec.unit ? ` ${spec.unit}` : ''}`
  const { p50, p95, format } = spec.percentiles
  return `p50 ${show(p50, format)} · p95 ${show(p95, format)}`
}

function specs(p: Overview, tick: (t: number) => string): Record<string, Spec[]> {
  const caption = when
  const colTick = (s: string) => tick(unix(s))
  const partialFrom = p.partial_from ? unix(p.partial_from) : undefined
  const lines = (compact: boolean, ls: Line[], format: (v: number) => string, empty: string, max?: number) => <>
    <LineChart compact={compact} lines={ls} format={format} tick={tick} caption={caption} emptyLabel={empty} max={max} partialFrom={partialFrom} />
    {!compact && partialFrom != null && ls.some(l => l.points.some(([t]) => t >= partialFrom)) &&
      <p className="muted tiny remi-bucket-size">Dashed end: the current {bucketSize(p.period)} so far</p>}</>
  const columns = (compact: boolean, data: ReturnType<typeof stacked>, empty: string) => {
    const grouped = countBuckets(data, p.period, compact ? 24 : 48)
    const cols = grouped.columns.map(c => ({ ...c, partial: partialFrom != null && unix(c.start) + grouped.seconds > partialFrom }))
    return <><ColumnChart compact={compact} columns={cols} series={data.legend} format={count}
      caption={start => `${when(unix(start))} – ${when(Math.min(unix(start) + grouped.seconds, grouped.end))}`}
      tick={colTick} emptyLabel={empty} />
      {!!grouped.columns.length && <p className="muted tiny remi-bucket-size">Totals per {bucketSize(grouped.seconds)}{cols.some(c => c.partial) ? ' · faded last column: so far' : ''}</p>}</>
  }
  /** Event counts with no point where nothing happened: zero there, for a continuous line. */
  const filled = (points: [number, number][]): [number, number][] => {
    if (!p.start || !points.length) return points
    const values = new Map(points), out: [number, number][] = []
    for (let t = unix(p.start); t <= (partialFrom ?? unix(p.end) - p.period); t += p.period) out.push([t, values.get(t) ?? 0])
    return out
  }
  const pair = (panel: string, p50: string, p95: string, format: (v: number) => string, label?: string): Percentiles =>
    ({ label, p50: latest(pick(p, panel, p50)?.points), p95: latest(pick(p, panel, p95)?.points), format })
  const pairLines = (panel: string, p50: string, p95: string, prefix = '') =>
    [line(p, panel, p50, C[0], `${prefix}p50`), line(p, panel, p95, C[1], `${prefix}p95`)]

  const free = p.free_slots
  // Without SlotsFree, REMI can't report free slots or jobs failed for good: a missing
  // JobsFailed would otherwise read as "0 failures".
  const newMetrics = p.reports?.slots_free ?? true
  const notYet = 'This metric is not being reported.'

  const warm = pick(p, 'first_step', 'fswarm'), cold = pick(p, 'first_step', 'fscold')
  const cohortLines = [line(p, 'first_step', 'fswarm', C[0], 'warm jobs'), line(p, 'first_step', 'fscold', C[1], 'cold jobs')]
  const st50 = pick(p, 'step', 'st50'), st95 = pick(p, 'step', 'st95')
  const failed = p.panels.failed_jobs ?? []
  const failedTotal = sum(failed)
  const attempts = p.panels.attempts ?? []
  const unscored = attempts.filter(s => s.label !== 'scored')
  const attemptsTotal = sum(attempts)
  const infraShare = attemptsTotal ? sum(attempts.filter(s => s.label === 'infrastructure')) / attemptsTotal : 0
  const ended = p.panels.nodes_ended ?? []
  const unexpected = ended.filter(s => ['lost', 'ended_by_amazon', 'preparation_failed', 'preparation_timeout', 'other'].includes(s.label))
  const lost = sum(ended.filter(s => s.label === 'lost'))
  const ready = pick(p, 'followers', 'ready')?.points ?? []
  const idle = pick(p, 'followers', 'idle')?.points ?? []
  const silent = pick(p, 'followers', 'silent')?.points ?? []
  const active: [number, number][] = ready.flatMap(([t, v]) => {
    const idleValue = idle.find(([u]) => u === t)?.[1]
    return idleValue == null ? [] : [[t, Math.max(0, v - idleValue)] as [number, number]]
  })
  const hostLines = (panel: string, ids: [string, string, string, string]) =>
    [['Memory', ids[0], C[0]], ['CPU', ids[1], C[1]], ['Busiest core', ids[2], C[2]], ['Disk', ids[3], C[3]]]
      .map(([label, id, color]) => line(p, panel, id, color, label))
  const hostTone = (panel: string, mem: string, disk: string) =>
    worst(byLimits(latest(pick(p, panel, mem)?.points), 85, 95), byLimits(latest(pick(p, panel, disk)?.points), 85, 95))
  const se = pick(p, 'api', 'se')
  const r95 = pick(p, 'api', 'r95')
  // Per route, one value for the whole window (backend api_routes).
  const routes = p.panels.api_routes ?? []
  const routeValue = (id: string, route: string) => routes.find(s => s.id === id && s.label === route)?.points.reduce((a, [, v]) => Math.max(a, v), 0)
  const routeTotal = (id: string, route: string) => sum(routes.filter(s => s.id === id && s.label === route))
  const slowRoutes = routes.filter(s => s.id === 'rt95' && s.points.length)
    .map(s => ({ label: s.label, p95: routeValue('rt95', s.label) ?? 0 })).sort((a, b) => b.p95 - a.p95).slice(0, 5)
  const errorRoutes = routes.filter(s => s.id === 'rte' && sum([s]) > 0)
    .map(s => ({ label: s.label, n: sum([s]) })).sort((a, b) => b.n - a.n).slice(0, 5)
  const seTotal = sum(se ? [se] : [])
  const pin = pick(p, 'db_pool', 'pin'), pmax = pick(p, 'db_pool', 'pmax'), pw = pick(p, 'db_pool', 'pw'), pto = pick(p, 'db_pool', 'pto')
  const dm = pick(p, 'db', 'dm'), dc = pick(p, 'db', 'dc')
  const followerReadings = latest(pick(p, 'follower_host', 'fn')?.points)
  const mr95 = pick(p, 'startup', 'mr95')
  const stages = (p.panels.startup ?? []).filter(s => s.id === 'stage50' || s.id === 'stage95')
  const stage95 = stages.filter(s => s.id === 'stage95' && s.points.length)
  const slowest = [...stage95].sort((a, b) => (latest(b.points) ?? 0) - (latest(a.points) ?? 0))[0]
  const machinesReady = sum((p.panels.startup ?? []).filter(s => s.id === 'mrn'))
  const startupLines = [line(p, 'startup', 'mr50', C[0], 'Launch → warm p50'), line(p, 'startup', 'mr95', C[1], 'Launch → warm p95'),
    ...stages.filter(s => s.id === 'stage50').map((s, i) => ({ label: `${s.label} p50`, color: C[2 + i] ?? C[3], points: s.points }))]
  const launchErrors = (p.panels.scaler ?? []).filter(s => s.id === 'le')
  const launchErrorsTotal = sum(launchErrors)
  const sreq = pick(p, 'scaler', 'sreq'), ssta = pick(p, 'scaler', 'ssta'), swr = pick(p, 'scaler', 'swr')
  const shortMax = swr?.points.length ? Math.max(...swr.points.map(([, v]) => v)) : undefined
  const slotLines = [{ label: 'Slots requested', color: C[0], points: filled(sreq?.points ?? []) },
    { label: 'Slots started', color: C[1], points: filled(ssta?.points ?? []) }]
  const pctStats = (panel: string, ids: [string, string][]) => ids.map(([id, label]) => ({ label, points: pick(p, panel, id)?.points ?? [], format: pct }))
  const apiStats = (ids: string[], format: (s: Series) => (v: number) => string) =>
    (p.panels.api ?? []).filter(s => ids.includes(s.id)).map(s => ({ label: s.label, points: s.points, format: format(s) }))
  const seriesStats = (panel: string, format: (s: Series) => (v: number) => string) =>
    (p.panels[panel] ?? []).map(s => ({ label: s.label, points: s.points, format: format(s) }))

  return {
    queue: [
      {
        key: 'idle-slots', title: 'Idle slots with queued jobs', sub: 'Free slots in pools that have jobs waiting',
        definition: 'For every pool with jobs waiting, the unused slots on its warm followers (machines still starting or preparing are not warm and not counted). A period counts only if both lasted all of it, and a slot is never counted against another pool’s jobs. The headline is the longest stretch in the selected window.',
        limits: 'Orange as soon as it happens; red when a stretch lasts 15 minutes or more.',
        tone: !newMetrics || !free.points.length ? 'none' : free.longest_seconds >= 900 ? 'bad' : free.longest_seconds > 0 ? 'warn' : 'ok',
        numericValue: newMetrics && free.points.length ? free.longest_seconds : undefined, improvement: 'down',
        value: !newMetrics ? '—' : free.longest_seconds ? secs(free.longest_seconds) : free.points.length ? 'None' : '—',
        detail: !newMetrics ? notYet : free.longest_seconds ? `Longest stretch ${secs(free.longest_seconds)}${free.pools.length ? ` · pool ${free.pools.join(', ')}` : ''}` : 'No stretch in this window',
        chart: c => lines(c, [{ label: 'Idle slots with queued jobs', color: C[0], points: free.points }], count, 'No queue data yet.'),
        stats: [{ label: 'Idle slots with queued jobs', points: free.points, format: count },
          { label: 'Jobs waiting', points: pick(p, 'queue', 'jw')?.points ?? [], format: count }],
        notable: { label: 'Idle slots while jobs waited', points: free.points, test: v => v > 0, format: v => `${count(v)} slots`, tone: 'warn' },
        alarms: ['PoolShort', 'FollowersIdle'],
      },
      {
        key: 'first-step', title: 'Time to first model step', sub: 'Job created → first step recorded · p50',
        definition: 'p50 time from job creation to the first model step, split by whether the assigned follower was warm. A follower is warm once it has passed REMI’s readiness check (prepared, image staged, matching its pool). Warm jobs ran on a follower that was warm before the job was created, even if that follower was busy. Cold jobs ran on a follower that became warm after the job was created. Samples are attempts whose first step was reported in the window, including retries; a retry is classified by the original job’s creation time. Missing readiness records are excluded, not guessed.',
        limits: 'Orange from 10 minutes at p50; red from 30 minutes, for either group.',
        tone: worst(byLimits(latest(warm?.points), 600, 1800), byLimits(latest(cold?.points), 600, 1800)),
        cohorts: { warm: latest(warm?.points), cold: latest(cold?.points) },
        detail: p.first_step_snapshot ? `Database reconstruction · ${when(unix(p.first_step_snapshot.start))} – ${when(unix(p.first_step_snapshot.end))} (Zurich)` : 'Warm and cold jobs, p50',
        chart: c => <>{lines(c, cohortLines, secs, 'Warm/cold measurements not reported yet.')}
          <Legend lines={cohortLines} format={secs} /></>,
        stats: [warm, cold].filter(Boolean).map(s => ({ label: s!.label, points: s!.points, format: secs })),
        alarms: [],
      },
      {
        key: 'step', title: 'Step latency', sub: 'Model request → step result',
        definition: 'For each step recorded in the minute: from the model request to the step’s result. Below it, the model’s answer alone (runtimes that report it); the rest of a step is the action.',
        limits: 'Orange from 30 s at p50 or 2 minutes at p95; red from 2 minutes at p50 or 5 at p95.',
        tone: worst(byLimits(latest(st50?.points), 30, 120), byLimits(latest(st95?.points), 120, 300)),
        percentiles: pair('step', 'st50', 'st95', secs),
        secondary: pair('step', 'mo50', 'mo95', secs, 'Model answer'),
        detail: st50?.points.length ? 'Model request to result, per step' : 'No steps in this window',
        chart: c => <>{lines(c, pairLines('step', 'st50', 'st95'), secs, 'No steps yet.')}
          <Legend lines={pairLines('step', 'st50', 'st95')} format={secs} /></>,
        stats: seriesStats('step', () => secs),
        notable: { label: 'Steps slower than 30 s (p50)', points: st50?.points ?? [], test: v => v >= 30, format: secs, tone: 'warn' },
        alarms: [],
      },
      {
        key: 'non-retriable', title: 'Non-retriable job failures', sub: 'Jobs failed with no retry left, by failure class',
        definition: 'Jobs REMI marked failed: the attempt was invalid, its failure class is not transient, it ran out of tries, or the model had already started. Grouped by the last attempt’s failure class.',
        limits: 'Orange from 1 in the window; red from 10.',
        tone: !newMetrics ? 'none' : byLimits(failed.length ? failedTotal : p.fleet_reporting ? 0 : undefined, 1, 10),
        numericValue: newMetrics && (failed.length || p.fleet_reporting) ? failedTotal : undefined, improvement: 'down',
        value: newMetrics ? count(failedTotal) : '—',
        detail: !newMetrics ? notYet : failed.length ? stacked(failed).legend.map(l => l.label).join(' · ') : 'None in this window',
        chart: c => columns(c, stacked(failed), 'No failed jobs.'),
        stats: failed.map(s => ({ label: s.label, points: s.points, format: count })),
        notable: { label: 'Jobs failed', points: totals(stacked(failed)), test: v => v > 0, format: v => `${count(v)} jobs`, tone: 'warn' },
        alarms: [], logs: 'errors',
      },
    ],
    followers: [
      {
        key: 'utilisation', title: 'Follower utilisation', sub: 'Warm followers running work, idle or silent',
        definition: 'Warm followers (passed REMI’s readiness check) by what they do: running at least one attempt (active), running nothing (idle), or not checked in for over a minute (silent). The leader gives a silent follower up after 5 minutes.',
        limits: 'Orange when the reported pool idle policy is exceeded; red while any follower is silent.',
        tone: !ready.length ? 'none' : (latest(silent) ?? 0) > 0 ? 'bad' : (latest(pick(p, 'followers', 'idle_overdue')?.points) ?? 0) > 0 ? 'warn' : 'ok',
        numericValue: latest(active), improvement: 'neutral',
        value: show(latest(active), count), unit: 'active',
        detail: `${show(latest(idle), count)} idle · ${show(latest(silent), count)} silent · ${show(latest(pick(p, 'followers', 'starting')?.points), count)} starting`,
        chart: c => lines(c, [{ label: 'Active', color: C[0], points: active }, { label: 'Idle', color: C[3], points: idle }, { label: 'Silent', color: C[2], points: silent }], count, 'No follower counts yet.'),
        stats: [{ label: 'Active', points: active, format: count }, ...seriesStats('followers', () => count)],
        notable: { label: 'Silent followers', points: silent, test: v => v > 0, format: v => `${count(v)} silent`, tone: 'bad' },
        alarms: ['FollowersSilent', 'FollowersIdle'],
      },
      {
        key: 'follower-startup', title: 'Follower startup', sub: 'Launch → warm, per machine',
        definition: 'For each follower that became warm (passed REMI’s readiness check: prepared, image staged, matching its pool): the time from its launch to warm (MachineReadySeconds), and the preparation stages the runtime timed on the way (PreparationStageSeconds per Stage; stage_image is putting the VM image on the machine). Reported in the minute the machine became warm; the percentiles are over the machines that became warm in the window or bucket. Machines that never became warm are counted in Follower terminations (preparation_failed, preparation_timeout).',
        limits: 'Orange from 15 minutes at p95; red from 30 minutes. From 16 to 20 Oct 2026, p95 was at most 10 minutes per day, and REMI gives a machine up after its pool’s preparation limit (45 minutes by default).',
        tone: byLimits(latest(mr95?.points), 900, 1800),
        percentiles: pair('startup', 'mr50', 'mr95', secs),
        secondary: slowest && { label: `Stage ${slowest.label}`, format: secs, p95: latest(slowest.points),
          p50: latest(stages.find(s => s.id === 'stage50' && s.label === slowest.label)?.points) },
        detail: machinesReady ? `${count(machinesReady)} ${machinesReady === 1 ? 'machine' : 'machines'} became warm` : 'No machine became warm in this window',
        chart: c => <>{lines(c, startupLines, secs, 'No machine became warm in this window.')}
          <Legend lines={startupLines} format={secs} /></>,
        stats: [...['mr50', 'mr95'].flatMap(id => (p.panels.startup ?? []).filter(s => s.id === id)).map(s => ({ label: s.label, points: s.points, format: secs })),
          ...stages.map(s => ({ label: `${s.label} ${s.id === 'stage50' ? 'p50' : 'p95'}`, points: s.points, format: secs })),
          { label: 'Machines became warm', points: pick(p, 'startup', 'mrn')?.points ?? [], format: count }],
        notable: { label: 'Launch to warm over 15 minutes (p95)', points: mr95?.points ?? [], test: v => v >= 900, format: secs, tone: 'warn' },
        alarms: [], logs: 'nodes',
      },
      {
        key: 'scaler', title: 'Scaler launches', sub: 'Launch errors by code · slots requested and started',
        definition: 'When a pool has more work than slots, the scaler asks Amazon for the missing slots (SlotsRequested) and counts the slots that came back (SlotsStarted; Amazon fills a request with whole machines, so this can exceed the request). LaunchErrors counts Amazon’s refusals by ErrorCode, e.g. InsufficientInstanceCapacity, or failed when the request itself raised an error. ShortWhenRequestedSeconds is how long the pool had already been short when the scaler asked: 0 on the first try, more while Amazon keeps refusing. All are reported only when the scaler asks.',
        limits: 'Orange from 1 launch error in the window; red when a pool had been short for 15 minutes or more when the scaler asked (the PoolShort alarm’s limit).',
        tone: !launchErrors.length && !p.fleet_reporting ? 'none' : (shortMax ?? 0) >= 900 ? 'bad' : launchErrorsTotal > 0 ? 'warn' : 'ok',
        numericValue: launchErrors.length || p.fleet_reporting ? launchErrorsTotal : undefined, improvement: 'down',
        value: count(launchErrorsTotal), unit: launchErrorsTotal === 1 ? 'launch error' : 'launch errors',
        detail: `${count(sum(sreq ? [sreq] : []))} slots requested · ${count(sum(ssta ? [ssta] : []))} started${shortMax ? ` · short up to ${secs(shortMax)} when asked` : ''}`,
        chart: c => <>{columns(c, stacked(launchErrors), 'No launch errors.')}
          {!c && <><p className="muted tiny remi-subchart">Slots requested and started, per {bucketSize(p.period)}</p>
            {lines(false, slotLines, count, 'The scaler asked for no machines.')}<Legend lines={slotLines} format={count} />
            <p className="muted tiny remi-subchart">How long the pool had been short when the scaler asked, longest per {bucketSize(p.period)}</p>
            {lines(false, [line(p, 'scaler', 'swr', C[2], 'Short when requested')], secs, 'No pool was short when the scaler asked.')}</>}</>,
        stats: [...launchErrors.map(s => ({ label: `LaunchErrors ${s.label}`, points: s.points, format: count })),
          ...[sreq, ssta].filter(Boolean).map(s => ({ label: s!.label, points: s!.points, format: count })),
          ...(swr ? [{ label: 'Short when requested', points: swr.points, format: secs }] : [])],
        notable: { label: 'Launch errors', points: totals(stacked(launchErrors)), test: v => v > 0, format: v => `${count(v)} errors`, tone: 'warn' },
        alarms: ['PoolShort'], logs: 'errors',
      },
      {
        key: 'follower-host', title: 'Follower host resources', sub: 'Memory, CPU and disk, %',
        definition: 'From the CloudWatch agent on each follower. Memory and disk show the fullest follower. CPU shows the average over all followers’ cores, and the busiest single core on any follower.',
        limits: 'Orange from 85% memory or disk; red from 95%. Red too when followers are warm but none reports.',
        tone: (latest(ready) ?? 0) > 0 && !followerReadings ? 'bad' : hostTone('follower_host', 'fm', 'fd'),
        numericValue: latest(pick(p, 'follower_host', 'fm')?.points), improvement: 'down',
        value: show(latest(pick(p, 'follower_host', 'fm')?.points), pct), unit: 'memory',
        detail: followerReadings ? `${count(followerReadings)} ${followerReadings === 1 ? 'reading' : 'readings'} in the last period` : 'No follower is reporting',
        chart: c => <>{lines(c, hostLines('follower_host', ['fm', 'fc', 'fk', 'fd']), pct, 'No follower readings in this window.', 100)}
          <Legend lines={hostLines('follower_host', ['fm', 'fc', 'fk', 'fd'])} format={pct} /></>,
        stats: pctStats('follower_host', [['fm', 'Memory (fullest)'], ['fc', 'CPU (all followers, average)'], ['fk', 'CPU (busiest core)'], ['fd', 'Disk (fullest)']]),
        notable: { label: 'Follower memory over 85%', points: pick(p, 'follower_host', 'fm')?.points ?? [], test: v => v >= 85, format: pct, tone: 'warn' },
        alarms: ['FollowerMemoryHigh', 'FollowersNotReporting', 'OutOfMemoryKill'], logs: 'nodes',
      },
      {
        key: 'terminations', title: 'Follower terminations', sub: 'Machines ended, by end reason',
        definition: 'Followers that ended in the minute and why: no_work and goodbye are normal scale-down; lost, ended_by_amazon (Spot reclaimed), preparation_failed and preparation_timeout are not.',
        limits: 'Orange from 1 unexpected termination; red from 3 lost followers.',
        tone: !ended.length && !p.fleet_reporting ? 'none' : lost >= 3 ? 'bad' : sum(unexpected) > 0 ? 'warn' : 'ok',
        numericValue: ended.length || p.fleet_reporting ? sum(ended) : undefined, improvement: 'neutral',
        value: count(sum(ended)), detail: ended.length ? `${count(sum(unexpected))} unexpected · ${stacked(ended).legend.map(l => l.label).join(' · ')}` : 'None in this window',
        chart: c => columns(c, stacked(ended), 'No machines ended.'),
        stats: ended.map(s => ({ label: s.label, points: s.points, format: count })),
        notable: { label: 'Unexpected terminations', points: totals(stacked(unexpected)), test: v => v > 0, format: v => `${count(v)} machines`, tone: 'warn' },
        alarms: ['FollowersSilent', 'ContainerCrashLoop'], logs: 'nodes',
      },
      {
        key: 'failed-attempts', title: 'Failed attempts', sub: 'Attempts ended without a score, by outcome',
        definition: 'Attempts that ended as infrastructure, invalid, stopped or evicted. An infrastructure attempt may be retried; the job’s final failure is counted in Non-retriable job failures.',
        limits: 'Orange when infrastructure failures exceed 5% of attempts; red above 20%.',
        tone: !attemptsTotal ? (p.fleet_reporting ? 'ok' : 'none') : byLimits(infraShare * 100, 5, 20),
        numericValue: attempts.length || p.fleet_reporting ? sum(unscored) : undefined, improvement: 'down',
        value: count(sum(unscored)), detail: `${count(sum(attempts.filter(s => s.label === 'scored')))} scored · ${pct(infraShare * 100)} infrastructure`,
        chart: c => columns(c, stacked(unscored), 'No failed attempts.'),
        stats: attempts.map(s => ({ label: s.label, points: s.points, format: count })),
        alarms: ['ContainerCrashLoop'], logs: 'errors',
      },
    ],
    leader: [
      {
        key: 'leader-host', title: 'Leader host resources', sub: 'Memory, CPU and disk, %',
        definition: 'From the CloudWatch agent on the leader’s machine. CPU is the whole machine on average, and its busiest single core (the leader is one process).',
        limits: 'Orange from 85% memory or disk; red from 95%.',
        tone: hostTone('leader_host', 'lm', 'ld'),
        numericValue: latest(pick(p, 'leader_host', 'lm')?.points), improvement: 'down',
        value: show(latest(pick(p, 'leader_host', 'lm')?.points), pct), unit: 'memory',
        detail: `CPU ${show(latest(pick(p, 'leader_host', 'lc')?.points), pct)} · disk ${show(latest(pick(p, 'leader_host', 'ld')?.points), pct)}`,
        chart: c => <>{lines(c, hostLines('leader_host', ['lm', 'lc', 'lk', 'ld']), pct, 'The leader sent no readings.', 100)}
          <Legend lines={hostLines('leader_host', ['lm', 'lc', 'lk', 'ld'])} format={pct} /></>,
        stats: pctStats('leader_host', [['lm', 'Memory'], ['lc', 'CPU (whole machine)'], ['lk', 'CPU (busiest core)'], ['ld', 'Disk (fullest)']]),
        notable: { label: 'Leader memory over 85%', points: pick(p, 'leader_host', 'lm')?.points ?? [], test: v => v >= 85, format: pct, tone: 'warn' },
        alarms: ['LeaderMemoryHigh', 'LeaderCoreBusy', 'LeaderDiskFull', 'LeaderNotReporting', 'LeaderStatusCheck', 'RemiLeader-MemoryTrend'],
      },
      {
        key: 'leader-5xx', title: 'Leader 5xx responses', sub: 'Server errors returned by the REMI API',
        definition: 'Responses with a 5xx status from the leader’s endpoints (ServerErrors), and in the detail by route (Route). Response time has its own card; the health probe from outside is in the alarms.',
        limits: 'Orange from 1 in the window; red from 10.',
        tone: byLimits(se ? seTotal : undefined, 1, 10),
        numericValue: se?.points.length ? seTotal : undefined, improvement: 'down',
        value: se ? count(seTotal) : '—',
        detail: !se?.points.length ? 'Not reported in this window' : seTotal ? 'In this window' : 'No server errors in this window',
        chart: c => columns(c, { columns: (se?.points ?? []).map(([t, v]) => ({ start: iso(t), parts: [v] })), legend: [{ label: '5xx responses', color: C[0] }] }, 'No server errors.'),
        stats: apiStats(['se', 'ce', 'rs'], () => count),
        notable: { label: '5xx responses', points: se?.points ?? [], test: v => v > 0, format: v => `${count(v)} responses`, tone: 'warn' },
        breakdown: { title: 'Server errors by route', note: 'Routes with any 5xx response in the selected window, most first.',
          columns: ['Route', '5xx', 'Responses'], empty: 'No route answered with a server error in this window.',
          rows: errorRoutes.map(r => ({ label: r.label, values: [count(r.n), count(routeTotal('rtn', r.label))] })) },
        alarms: ['LeaderServerErrors', 'LeaderUnhealthy'], logs: 'server_errors',
      },
      {
        key: 'leader-response', title: 'Leader response time', sub: 'Request received → response sent, all routes · p50/p95',
        definition: 'Time from the leader receiving a request to sending its response (ResponseMs), over all its endpoints; the detail lists the slowest routes (Route) by p95 over the selected window. The health probe from outside is in the alarms.',
        limits: 'Orange from 5 s at p95, the threshold of REMI’s LeaderResponsesSlow alarm; red from 15 s. From 16 to 20 Oct 2026, the daily p95 was 0.6–2.1 s and the 15-minute p95 had a median of 0.34 s, exceeded 5 s in 10 of 315 quarter-hours and never reached 15 s.',
        tone: byLimits(latest(r95?.points), 5000, 15000),
        percentiles: pair('api', 'r50', 'r95', ms),
        detail: r95?.points.length ? `${count(sum((p.panels.api ?? []).filter(s => s.id === 'rs')))} responses` : 'No responses reported in this window',
        chart: c => <>{lines(c, pairLines('api', 'r50', 'r95'), ms, 'No responses in this window.')}
          <Legend lines={pairLines('api', 'r50', 'r95')} format={ms} /></>,
        stats: apiStats(['r50', 'r95', 'rs', 'hs'], s => s.id === 'hs' ? secs : s.id === 'rs' ? count : ms),
        notable: { label: 'Responses slower than 5 s (p95)', points: r95?.points ?? [], test: v => v >= 5000, format: ms, tone: 'warn' },
        breakdown: { title: 'Slowest routes', note: 'p95 response time per route over the selected window, the slowest five.',
          columns: ['Route', 'p95', 'Responses'], empty: 'No per-route measurements in this window.',
          rows: slowRoutes.map(r => ({ label: r.label, values: [ms(r.p95), count(routeTotal('rtn', r.label))] })) },
        alarms: ['LeaderResponsesSlow', 'LeaderSlow'],
      },
      {
        key: 'db-pool', title: 'DB connection pool', sub: 'Leader connections in use, limit, queued requests',
        definition: 'The leader’s pool of PostgreSQL connections: in use, its limit, requests waiting for a connection, the time all requests spent waiting, and requests that got no connection (they timed out after 30 s, or the queue was full).',
        limits: 'Orange while requests wait; red when any request got no connection.',
        tone: !pin?.points.length ? 'none' : sum(pto ? [pto] : []) > 0 ? 'bad' : (latest(pw?.points) ?? 0) > 0 ? 'warn' : 'ok',
        numericValue: latest(pin?.points), improvement: 'neutral',
        value: show(latest(pin?.points), count), unit: `of ${show(latest(pmax?.points), count)}`,
        detail: `${show(latest(pw?.points), count)} waiting · ${count(sum(pto ? [pto] : []))} got no connection in window`,
        chart: c => lines(c, [line(p, 'db_pool', 'pin', C[0], 'In use'), line(p, 'db_pool', 'pmax', C[3], 'Limit', true), line(p, 'db_pool', 'pw', C[2], 'Waiting')], count, 'No connection data.'),
        stats: seriesStats('db_pool', s => s.id === 'pwm' ? ms : count),
        notable: { label: 'Requests queued for a connection', points: pw?.points ?? [], test: v => v > 0, format: v => `${count(v)} waiting`, tone: 'warn' },
        alarms: ['LeaderPoolQueue', 'LeaderPoolTimeouts'],
      },
      {
        key: 'rds', title: 'RDS instance', sub: `remi-${p.environment}: free memory, CPU, connections`,
        definition: 'Amazon RDS metrics for REMI’s PostgreSQL instance: freeable memory, CPU, open connections, read and write latency, disk queue and free storage.',
        limits: 'Orange from 75% CPU or under 512 MB free memory; red from 90% CPU or under 256 MB.',
        tone: worst(byLimits(latest(dc?.points), 75, 90), dm?.points.length ? byLimits(-(latest(dm.points)!), -512e6, -256e6) : 'none'),
        numericValue: latest(dm?.points), improvement: 'up',
        value: show(latest(dm?.points), gb), unit: 'free',
        detail: `CPU ${show(latest(dc?.points), pct)} · ${show(latest(pick(p, 'db', 'dn')?.points), count)} connections · write ${show(latest(pick(p, 'db', 'dwl')?.points), secs)}`,
        chart: c => <>{lines(c, [line(p, 'db', 'dc', C[0], 'CPU')], pct, 'No database data.', 100)}
          <Legend lines={[line(p, 'db', 'dm', C[1], 'Free memory')]} format={gb} />
          <Legend lines={[line(p, 'db', 'drl', C[2], 'Read latency'), line(p, 'db', 'dwl', C[3], 'Write latency')]} format={secs} /></>,
        stats: (p.panels.db ?? []).map(s => ({ label: s.label.replace(/ \((bytes|s)\)$/, ''), points: s.points,
          format: s.id === 'dm' || s.id === 'ds' ? gb : s.id === 'drl' || s.id === 'dwl' ? secs : s.id === 'dc' ? pct : count })),
        notable: { label: 'Database CPU over 75%', points: dc?.points ?? [], test: v => v >= 75, format: pct, tone: 'warn' },
        alarms: ['RemiDb-FreeStorageLow', 'RemiDb-FreeableMemoryLow', 'RemiDb-SwapHigh'],
      },
    ],
  }
}

// --- Page ------------------------------------------------------------------------------------

type Detail = { kind: 'tile'; key: string } | { kind: 'alarm'; alarm: Alarm } | { kind: 'log'; row: LogRow }
const alarmId = (a: Alarm) => a.id ?? `${a.source ?? 'remi'}:${a.name}`

function RangeSelect({ value, onChange, label }: { value: Window; onChange: (value: Window) => void; label: string }) {
  return <label className="remi-range"><span className="muted tiny">Range</span>
    <select aria-label={`${label} time range`} value={value} onChange={event => onChange(event.target.value as Window)}>
      {WINDOWS.map(([key, name]) => <option key={key} value={key}>{key === 'all' ? name : key}</option>)}
    </select>
  </label>
}

function useCardRange(key: string): [Window, (value: Window) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(`range-${key}`)
  return [WINDOWS.some(([k]) => k === value) ? value as Window : '24h', value => setParams(old => {
    const next = new URLSearchParams(old)
    next.set(`range-${key}`, value)
    return next
  })]
}

export default function Remi() {
  const [params, setParams] = useSearchParams()
  const environment = (['production', 'staging'].includes(params.get('environment') ?? '') ? params.get('environment') : '') as Environment | ''
  const [log, setLog] = useState<LogRow | null>(null)
  const [now, setNow] = useState(Date.now)
  const state = useBoardData<LiveStatus>(queryPath('/eng/remi/status', { environment }), true)
  const { reload } = state
  useEffect(() => {
    const timer = globalThis.setInterval(() => { void reload() }, REFRESH_MS)
    const clock = globalThis.setInterval(() => setNow(Date.now()), 1000)
    return () => { globalThis.clearInterval(timer); globalThis.clearInterval(clock) }
  }, [reload])
  const page = state.data
  const env = environment || page?.environment || 'production'
  const open = (value: Detail | null) => {
    setLog(value?.kind === 'log' ? value.row : null)
    setParams(old => { const next = new URLSearchParams(old); next.delete('alarm'); next.delete('tile')
      if (value?.kind === 'alarm') next.set('alarm', alarmId(value.alarm))
      if (value?.kind === 'tile') next.set('tile', value.key)
      if (page) next.set('environment', page.environment)
      return next })
  }
  const controls = <div className="remi-controls">
    <span className="muted small">{state.receivedAt ? `last polled ${Math.max(0, Math.floor((now - state.receivedAt) / 1000))} secs ago` : 'Polling…'}</span>
    <div className="segmented" role="group" aria-label="Environment">
      {(['production', 'staging'] as const).map(e => <button type="button" key={e} aria-pressed={env === e}
        className={env === e ? 'selected' : ''} onClick={() => {
          setLog(null)
          setParams(old => { const next = new URLSearchParams(old); next.set('environment', e); next.delete('alarm'); next.delete('tile'); return next })
        }}>{e === 'production' ? 'Production' : 'Staging'}</button>)}
    </div>
    <button type="button" className="ghost remi-refresh" onClick={() => void reload(true)} disabled={state.loading}
      aria-label="Refresh now"><Icon name="refresh" size={15} /></button>
  </div>
  const selectedAlarm = page?.alarms.find(a => alarmId(a) === params.get('alarm'))
  // Only the card definitions are needed here. Each card loads the last 24 hours.
  const groups = useMemo(() => page ? specs({ ...page, window: 'all', period: 86400, start: '', end: '', panels: {},
    free_slots: { points: [], current_seconds: 0, longest_seconds: 0, pools: [] }, fleet_reporting: false }, day) : null, [page])
  return <div className="remi-page">
    <h1 className="visually-hidden">REMI</h1>
    {!page && <div className="remi-topbar">{controls}</div>}
    {state.error && <div className="note err" role="alert">{page ? 'Refresh failed. Showing last known values.' : 'Couldn’t load REMI’s status.'} {state.error}</div>}
    {!page && !state.error && <p className="muted">Loading REMI…</p>}
    {page && groups && <>
      <Status page={page} refreshFailed={Boolean(state.error)} now={now} controls={controls} />
      <p className="muted tiny">{page.simulated && 'Simulated example data · '}All times are Zurich time (CET/CEST).</p>
      <div className="remi-layout">
        <div className="remi-main">
          {([['queue', 'Queue and latency', 'Job waits, latency and failures.'], ['followers', 'Followers', 'The machines that run rollouts.'],
            ['leader', 'Leader and database', 'REMI’s API service and its PostgreSQL.']] as const).map(([key, title, lead]) =>
            <section key={key} className="remi-card" aria-labelledby={`remi-${key}`}><CardHead id={`remi-${key}`} title={title} lead={lead} />
              <div className="remi-grid">{groups[key].map(spec => <MetricCard key={`${env}:${spec.key}`} card={spec} environment={page.environment}
                selected={params.get('tile') === spec.key} open={open} />)}</div>
            </section>)}
        </div>
        <div className="remi-side">
          <Alarms key={env} page={page} open={open} />
          <Logs key={env} environment={page.environment} open={open} />
        </div>
      </div>
    </>}
    {page && params.get('alarm') && !selectedAlarm && <p className="note info">The selected alarm is not present in {page.environment}.</p>}
    {page && selectedAlarm && <AlarmDetail alarm={selectedAlarm} page={page} onClose={() => open(null)} />}
    {log && <LogDetail row={log} onClose={() => open(null)} />}
  </div>
}

function MetricCard({ card, environment, selected, open }: { card: Spec; environment: Environment; selected: boolean; open: (d: Detail | null) => void }) {
  const state = useBoardData<Overview>(queryPath('/eng/remi/overview', { environment, window: '24h' }))
  const { reload } = state
  useEffect(() => {
    if (!CURRENT_CARDS.has(card.key)) return
    const timer = globalThis.setInterval(() => { void reload() }, REFRESH_MS)
    return () => globalThis.clearInterval(timer)
  }, [card.key, reload])
  const page = state.data
  const spec = useMemo(() => {
    if (!page) return card
    const history = Object.values(specs(page, time)).flat().find(s => s.key === card.key)!
    const done = Object.values(specs(complete(page), time)).flat().find(s => s.key === card.key)!
    const current = CURRENT_CARDS.has(card.key)
    const source = current ? { ...page, panels: state.error ? {} : page.current_panels ?? {} } : page.summary ?? { ...page, panels: {} }
    const headline = Object.values(specs(source, time)).flat().find(s => s.key === card.key)!
    if (current) {
      headline.sub += ' · latest reading'
      if (card.key === 'db-pool') headline.detail = `${show(latest(pick(source, 'db_pool', 'pw')?.points), count)} waiting`
    }
    return { ...headline, chart: history.chart, stats: done.stats, notable: done.notable }
  }, [page, card, state.error])
  return <>
    <Tile spec={spec} previous={page?.previous ? Object.values(specs(page.previous, time)).flat().find(s => s.key === card.key) : undefined} open={open} loading={state.loading} error={state.error} />
    {selected && page && <MetricDetail card={card} initial={page} open={open} />}
  </>
}

function MetricDetail({ card, initial, open }: { card: Spec; initial: Overview; open: (d: Detail | null) => void }) {
  const [window, setWindow] = useCardRange(card.key)
  const state = useBoardData<Overview>(queryPath('/eng/remi/overview', { environment: initial.environment, window }))
  const page = state.data ?? initial
  const spec = useMemo(() => {
    const tick = page.window === 'all' || page.window === '7d' ? day : time
    const find = (p: Overview) => Object.values(specs(p, tick)).flat().find(s => s.key === card.key)!
    return { ...find(complete(page)), chart: find(page).chart }
  }, [page, card.key])
  return <TileDetail spec={spec} page={page} window={window} onClose={() => open(null)} open={open}
    range={<><RangeSelect label={card.title} value={window} onChange={setWindow} />
      {state.loading && <p className="muted tiny">Loading selected range…</p>}
      {state.error && <p className="note err" role="alert">History unavailable: {state.error}</p>}</>}
    loading={!state.data} />
}

// The areas under the status, in the order shown: each has its alarms, and REMI's three a
// live check. An area is red when its check fails or one of its alarms fires.
const CHECKS = [
  { area: 'Leader', check: 'Leader answers /health' },
  { area: 'Followers', check: 'Followers report their machines' },
  { area: 'Database', check: 'No requests queue for the database' },
  { area: 'Agents', check: 'No agent is waiting for a developer' },
]
/** Which area an alarm is about: the authoring agents' own (NotifyDev), the leader's
 *  connection pool as the database, the rest of the leader's alarms as the leader, and
 *  everything else about the followers. */
function areaOf(alarm: Alarm) {
  if (alarm.source === 'agents') return 'Agents'
  // The monitoring stack's alarms are named RemiDb-… and RemiLeader-….
  const name = alarm.name.replace(/^Remi(?=[A-Z])/, '')
  if (/^LeaderPool|Database|Rds|^Db-/.test(name)) return 'Database'
  if (name.startsWith('Leader')) return 'Leader'
  return 'Followers'
}

function Status({ page, refreshFailed, now, controls }: { page: LiveStatus; refreshFailed: boolean; now: number; controls: ReactNode }) {
  const firing = page.alarms.filter(a => a.state === 'ALARM')
  const freshness = (page.status.freshness_seconds ?? 180) * 1000
  const expired = !page.status.evaluated_at || now - Date.parse(page.status.evaluated_at) > freshness
  const unavailable = refreshFailed || expired
  const checks = CHECKS.map(c => {
    const check = page.status.checks.find(x => x.name === c.check)
    const stale = check?.state === 'stale' || Boolean(check?.observed_at && now - Date.parse(check.observed_at) > freshness)
    const ok = unavailable || stale ? null : check?.ok ?? null
    const alarms = page.alarms.filter(a => areaOf(a) === c.area)
    const fired = alarms.filter(a => a.state === 'ALARM')
    const blind = alarms.filter(a => a.state === 'INSUFFICIENT_DATA')
    const tone: Tone = unavailable ? 'warn' : ok === false || fired.length ? 'bad' : blind.length || ok === null ? 'warn' : 'ok'
    const detail = unavailable ? 'Current evaluation unavailable' : stale ? 'Measurement is stale' : check?.detail ?? 'Not reporting'
    const why = [`${detail}${ok === null ? ' · unknown' : ok ? '' : ' · failing'}${check?.observed_at ? ` · observed ${time(unix(check.observed_at))}` : ''}`,
      ...fired.map(a => `${a.name} ${unavailable ? 'last reported firing' : 'firing'}`), ...blind.map(a => `${a.name} has no data`)]
    return { ...c, ok, tone, why }
  })
  const noData = page.alarms.filter(a => a.state === 'INSUFFICIENT_DATA')
  const tone: Tone = unavailable ? 'warn' : firing.length || checks.some(c => c.tone === 'bad') ? 'bad'
    : page.status.level === 'unknown' || noData.length || checks.some(c => c.tone === 'warn') ? 'warn' : 'ok'
  const failedChecks = checks.filter(c => c.ok === false).length
  const head = unavailable ? 'Current status unavailable' : tone === 'ok' ? 'Current checks are passing'
    : firing.length === 1 ? `Alarm firing: ${firing[0].name}` : firing.length ? `${firing.length} alarms firing`
      : tone === 'bad' ? `${failedChecks} ${failedChecks === 1 ? 'check is' : 'checks are'} failing` : 'Current status is unknown'
  return <section className={`remi-status tone-${tone}`} aria-live="polite">
    <div className="remi-status-head">
      {!unavailable && firing.length > 0 && <Fire />}
      <span className="remi-status-mark">{tone === 'ok' ? 'OK' : <Icon name="alert" size={22} />}</span>
      <div>
        <h2>{head}</h2>
        <p>{unavailable ? `Showing the last known evaluation${page.status.evaluated_at ? ` from ${when(unix(page.status.evaluated_at))}` : ''}.`
          : tone === 'ok' ? `Recent checks pass and no CloudWatch alarm is firing in ${page.environment}.`
          : firing.length ? firing.map(a => a.description || a.name).join(' · ')
              : checks.some(c => c.ok !== true) ? checks.filter(c => c.ok !== true).flatMap(c => c.why).join(' · ')
                : `${noData.length} ${noData.length === 1 ? 'alarm has' : 'alarms have'} no data to evaluate.`}</p>
      </div>
      {controls}
    </div>
    <ul className="remi-checks" aria-label="Checks">
      {checks.map(c => <li key={c.area} className={`tone-${c.tone}`}>
        <span className="remi-check-area">{c.area}</span>
        <span className="remi-check-text is-why">{c.why.join(' · ')}</span>
        <Square tone={c.tone} />
      </li>)}
    </ul>
  </section>
}

/** Flames rising from the banner's bottom edge while an alarm fires. Decorative: the
 *  headline says what is wrong. Particles are spread deterministically, so they don't move
 *  around on every refresh. */
function Fire() {
  const flames = Array.from({ length: 90 }, (_, i) => {
    const r = (n: number) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1
    return { x: (i / 90) * 100 + r(1) * 1.5, size: 34 + r(2) * 46, duration: 0.8 + r(3) * 0.8, delay: -r(4) * 1.6, rise: 45 + r(5) * 85 }
  })
  return <div className="remi-fire" aria-hidden="true">{flames.map((f, i) => <i key={i} style={{
    left: `${f.x}%`, width: f.size, height: f.size, animationDuration: `${f.duration}s`, animationDelay: `${f.delay}s`,
    ['--rise' as string]: `${f.rise}px` }} />)}</div>
}

function CardHead({ id, title, lead, aside }: { id: string; title: string; lead: string; aside?: ReactNode }) {
  return <div className="remi-card-head">
    <h2 id={id}>{title}</h2>{aside}<p className="lead">{lead}</p>
  </div>
}

/** p50 and p95 side by side, the same size: neither is the headline over the other. */
function Pair({ pair, small = false, previous, compare = false }: { pair: Percentiles; small?: boolean; previous?: Percentiles; compare?: boolean }) {
  return <div className={`remi-pair${small ? ' small' : ''}`}>
    {pair.label && <span className="remi-pair-label">{pair.label}</span>}
    <div><span>p50</span><strong>{pair.p50 == null ? 'No data' : pair.format(pair.p50)}</strong>{compare && <Change current={pair.p50} previous={previous?.p50} improvement="down" basis="p50 across all samples in each 24h period" />}</div>
    <div><span>p95</span><strong>{pair.p95 == null ? 'No data' : pair.format(pair.p95)}</strong>{compare && <Change current={pair.p95} previous={previous?.p95} improvement="down" basis="p95 across all samples in each 24h period" />}</div>
  </div>
}

function Cohorts({ values, previous, compare = false }: { values: NonNullable<Spec['cohorts']>; previous?: Spec['cohorts']; compare?: boolean }) {
  return <div className="remi-pair">{(['warm', 'cold'] as const).map(key => <div key={key}>
    <span>{key} jobs</span><strong>{values[key] == null ? 'No data' : secs(values[key]!)}</strong>
    {compare && <Change current={values[key]} previous={previous?.[key]} improvement="down" basis={`p50 across ${key} jobs in each 24h period`} />}
  </div>)}</div>
}

function Change({ current, previous, improvement = 'neutral', basis = 'Full-period statistic for each 24h period' }: { current?: number; previous?: number; improvement?: Spec['improvement']; basis?: string }) {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null
  }
  const direction = current > previous ? 'up' : current < previous ? 'down' : 'same'
  const tone = direction === 'same' || improvement === 'neutral' ? 'none' : direction === improvement ? 'ok' : 'bad'
  const percentage = previous === 0 ? current === 0 ? '0%' : 'new' : `${Math.abs((current - previous) / previous * 100).toFixed(1)}%`
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→'
  return <span className={`remi-change tone-${tone}`} tabIndex={0} data-tooltip={`${basis}: ${current} vs ${previous} in the preceding 24h${previous === 0 && current !== 0 ? '; percentage undefined from zero' : ''}`}
    aria-label={`${direction === 'same' ? 'Unchanged' : direction === 'up' ? 'Increase' : 'Decrease'} ${percentage} vs preceding 24h`}>
    {arrow} {percentage}
  </span>
}

/** A status as a plain square in its colour; its name is for screen readers and on hover. */
function Square({ tone }: { tone: Tone }) {
  return <span className={`remi-square tone-${tone}`} role="img" aria-label={TONE_LABEL[tone]} title={TONE_LABEL[tone]} />
}

function Tile({ spec, previous, open, loading, error }: { spec: Spec; previous?: Spec; open: (d: Detail) => void; loading: boolean; error: string | null }) {
  return <article className={`run-metric remi-tile tone-${spec.tone}`}>
    <button type="button" className="remi-tile-open" onClick={() => open({ kind: 'tile', key: spec.key })}
      aria-label={`${spec.title}: details and timeline`} />
    <div className="remi-tile-top">
      <h3>{spec.title}</h3>
      <Square tone={spec.tone} />
    </div>
    {loading && <p className="muted tiny">Loading history…</p>}
    {error && <p className="note err" role="alert">History unavailable: {error}</p>}
    <p className="remi-tile-sub">{spec.sub}</p>
    {spec.cohorts ? <Cohorts values={spec.cohorts} previous={previous?.cohorts} compare /> : spec.percentiles
      ? <Pair pair={spec.percentiles} previous={previous?.percentiles} compare />
      : <strong className="run-metric-value">{spec.numericValue == null ? 'No data' : spec.value}{spec.numericValue != null && spec.unit && <small> {spec.unit}</small>} {!CURRENT_CARDS.has(spec.key) && <Change current={spec.numericValue} previous={previous?.numericValue} improvement={spec.improvement} basis={['non-retriable', 'terminations', 'failed-attempts', 'leader-5xx', 'scaler'].includes(spec.key) ? 'Total in each 24h period' : spec.key === 'idle-slots' ? 'Longest stretch in each 24h period' : 'Average over each 24h period'} />}</strong>}
    {spec.secondary && <Pair pair={spec.secondary} previous={previous?.secondary} small compare />}
    <p className="remi-tile-detail">{spec.detail}</p>
    <div className="metric-trend">{CURRENT_CARDS.has(spec.key) && <p className="muted tiny">History · last 24 hours</p>}{spec.chart(true)}</div>
  </article>
}

// --- Alarms ----------------------------------------------------------------------------------

const alarmTone = (a: Alarm): Tone => a.state === 'ALARM' ? 'bad' : a.state === 'OK' ? 'ok' : 'warn'
const alarmLabel = (a: Alarm) => a.state === 'ALARM' ? 'Firing' : a.state === 'OK' ? 'OK' : 'No data'

function Alarms({ page, open }: { page: LiveStatus; open: (d: Detail) => void }) {
  const [showPassing, setShowPassing] = useState(false)
  // Firing before no data, whatever order they arrive in.
  const attention = page.alarms.filter(a => a.state !== 'OK').sort((a, b) => Number(b.state === 'ALARM') - Number(a.state === 'ALARM'))
  const passing = page.alarms.filter(a => a.state === 'OK')
  const firing = attention.filter(a => a.state === 'ALARM').length
  return <section className="remi-card" aria-labelledby="remi-alarms">
    <CardHead id="remi-alarms" title="Alarms" lead={`CloudWatch alarms of REMI and the authoring agents for ${page.environment}${page.environment === 'production' ? ', including the monitoring stack’s (seldon-prod-)' : ''}. Shown here when one fires (red) or has no data to judge by (orange); the rest are folded away.`}
      aside={<span className={`remi-state tone-${firing ? 'bad' : attention.length ? 'warn' : 'ok'}`}>{firing ? `${firing} firing` : attention.length ? `${attention.length} without data` : 'OK'}</span>} />
    {!attention.length && <p className="remi-all-clear"><strong>OK</strong> No alarm is firing.</p>}
    {!!attention.length && <AlarmRows alarms={attention} open={open} />}
    {!!passing.length && <button type="button" className="ghost small remi-fold" aria-expanded={showPassing} onClick={() => setShowPassing(!showPassing)}>
      <Icon name="chevron" size={14} /> {showPassing ? 'Hide' : 'Show'} {attention.length ? `the other ${passing.length}` : `all ${passing.length}`} {passing.length === 1 ? 'alarm' : 'alarms'}</button>}
    {showPassing && <AlarmRows alarms={passing} open={open} />}
    <PastAlarms environment={page.environment} />
  </section>
}

interface PastAlarm { id: string; name: string; source: string; at: string; reason: string; summary: string }
function PastAlarms({ environment }: { environment: Environment }) {
  const state = useBoardData<{ events: PastAlarm[] }>(queryPath('/eng/remi/alarm-history', { environment }))
  const { reload } = state
  useEffect(() => {
    const timer = globalThis.setInterval(() => { void reload() }, REFRESH_MS)
    return () => globalThis.clearInterval(timer)
  }, [reload])
  const events = [...(state.data?.events ?? [])].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
  return <div className="remi-past-alarms">
    <h3>Past alarms</h3>
    <p className="muted tiny">Recorded firing events over the retained 30 days, newest first.</p>
    {state.error && <p className="note err" role="alert">Alarm history unavailable: {state.error}</p>}
    {state.loading && !events.length && <p className="empty">Loading past alarms…</p>}
    {!state.loading && !events.length && <p className="empty">No recorded alarms.</p>}
    {!!events.length && <ol className="remi-alarm-events" aria-label="Past alarms" tabIndex={0}>
      {events.map(event => <li key={`${event.id}:${event.at}`}>
        <time dateTime={event.at}>{when(unix(event.at))}</time>
        <strong>{event.name}</strong>
        <p>{event.reason || event.summary}</p>
      </li>)}
    </ol>}
  </div>
}

function AlarmRows({ alarms, open }: { alarms: Alarm[]; open: (d: Detail) => void }) {
  return <ul className="remi-alarm-list">{alarms.map(a => <li key={alarmId(a)} className={`tone-${alarmTone(a)}`}>
    <button type="button" onClick={() => open({ kind: 'alarm', alarm: a })}>
      <span className={`chip tone-${alarmTone(a)}`}>{alarmLabel(a)}</span>
      <span><strong>{a.name}</strong><span className="remi-alarm-text">{a.description}</span></span>
      <time className="muted tiny" title={when(unix(a.since))}>since {when(unix(a.since))}</time>
    </button>
  </li>)}</ul>
}

// --- Logs ------------------------------------------------------------------------------------

type LogKind = 'errors' | 'server_errors' | 'nodes' | 'search'
/** `additional_to` marks a secondary runtime record (an upload or cleanup failure after the
 *  attempt's own): the same attempt's failure, not a separate one. */
interface LogRow { timestamp: string; stream: string; message: string; additional_to?: string }
interface LogResult { status: string; rows: LogRow[]; scanned_bytes?: number; matched?: number }
const LOG_KINDS: [LogKind, string][] = [['search', 'Search'], ['errors', 'Errors'], ['server_errors', '5xx'], ['nodes', 'Node events']]

/** Starts a Logs Insights query and reads it back until it's done (a few seconds). */
function useLogQuery(environment: Environment, window: Window, selection = '') {
  const [result, setResult] = useState<LogResult | null>(null)
  const [error, setError] = useState('')
  const [running, setRunning] = useState(false)
  const run = useRef(0)
  const [scope, setScope] = useState('')
  const currentScope = `${environment}:${window}:${selection}`
  useEffect(() => () => { run.current++ }, [currentScope])
  const start = async (kind: LogKind, text = '') => {
    const mine = ++run.current
    setScope(currentScope)
    setRunning(true); setError(''); setResult(null)
    try {
      const { query_id } = await api.post<{ query_id: string }>('/eng/remi/logs', { kind, text: text || undefined, environment, window }, { notifyBoardChange: false })
      for (let i = 0; i < 40 && run.current === mine; i++) {
        const answer = await api.get<LogResult>(`/eng/remi/logs/${query_id}`)
        if (run.current !== mine) return
        setResult(answer)
        if (!['Scheduled', 'Running'].includes(answer.status)) break
        await new Promise(r => setTimeout(r, 1000))
      }
    } catch (e) {
      if (run.current === mine) setError(e instanceof Error ? e.message : 'The query failed.')
    } finally {
      if (run.current === mine) setRunning(false)
    }
  }
  return { result: scope === currentScope ? result : null, error: scope === currentScope ? error : '',
    running: scope === currentScope && running, start }
}

function parseLog(row: LogRow) {
  try {
    const j = JSON.parse(row.message) as Record<string, unknown>
    return { severity: j.severity as string | undefined, text: String(j.message ?? row.message), component: j.component as string | undefined, fields: j }
  } catch {
    const status = row.message.match(/" (\d{3}) /)?.[1]
    return { severity: status?.startsWith('5') ? 'ERROR' : undefined, text: row.message, component: undefined, fields: null }
  }
}
const logTone = (severity?: string): Tone => severity === 'ERROR' || severity === 'CRITICAL' ? 'bad' : severity === 'WARNING' ? 'warn' : 'none'

function Logs({ environment, open }: { environment: Environment; open: (d: Detail) => void }) {
  const window: Window = '24h'
  const [kind, setKind] = useState<LogKind>('search')
  const [text, setText] = useState('')
  const [submitted, setSubmitted] = useState('')
  const { result, error, running, start } = useLogQuery(environment, window, `${kind}:${submitted}`)
  // An empty search lists recent logs; typing does not run a query until submitted.
  useEffect(() => { void start(kind, kind === 'search' ? submitted : '') }, [kind, submitted, environment, window]) // eslint-disable-line react-hooks/exhaustive-deps

  return <section className="remi-card" aria-labelledby="remi-logs">
    <CardHead id="remi-logs" title="Logs" lead={`Search the last 24 hours of /remi/${environment} and its host events, or pick a prepared query. Newest first. An empty search shows recent logs; enter text to filter them.`} />
    <div className="remi-toolbar">
      <div className="segmented" role="group" aria-label="Log query">
        {LOG_KINDS.map(([key, name]) => <button type="button" key={key} aria-pressed={kind === key}
          className={kind === key ? 'selected' : ''} onClick={() => setKind(key)}>{name}</button>)}
      </div>
      <span className="muted tiny">{running ? '' : result ? `${result.rows.length} lines${result.scanned_bytes ? ` · read ${(result.scanned_bytes / 1e6).toFixed(1)} MB` : ''}` : ''}</span>
    </div>
    {kind === 'search' && <form className="remi-form" role="search" onSubmit={event => { event.preventDefault(); const query = text.trim(); if (query === submitted) void start('search', query); else setSubmitted(query) }}>
      <input className="remi-field" type="text" value={text} maxLength={200} onChange={event => setText(event.target.value)}
        placeholder="Diagnostic code, diagnostic ID, attempt ID, or message text" aria-label="Search REMI's logs" />
      <button type="submit" className="remi-button" disabled={running}><Icon name="search" size={15} /> Search</button>
    </form>}
    {running && <div className="remi-log-loading" role="status" aria-live="polite">
      <span className="remi-log-spinner" aria-hidden="true" />
      <div><strong>{!result ? 'Starting log query…' : result.status === 'Scheduled' ? 'Log query queued…' : 'Loading logs…'}</strong>
        <span className="muted tiny">{result?.rows.length ? `${result.rows.length} ${result.rows.length === 1 ? 'line' : 'lines'} found so far · results are still loading` : 'Waiting for log results'}{result?.scanned_bytes ? ` · read ${(result.scanned_bytes / 1e6).toFixed(1)} MB` : ''}</span>
      </div>
    </div>}
    {error && <div className="note err" role="alert">{error}</div>}
    {result && !result.rows.length && !running && <p className="empty">Nothing in this window.</p>}
    {!!result?.rows.length && <LogLines rows={result.rows} open={open} />}
  </section>
}

function LogLines({ rows, open }: { rows: LogRow[]; open: (d: Detail) => void }) {
  return <ol className="remi-log-lines" aria-label="Log entries" tabIndex={0}>{[...rows].sort((a, b) => (unix(b.timestamp) || 0) - (unix(a.timestamp) || 0)).map((row, i) => {
    const parsed = parseLog(row)
    const tone = logTone(parsed.severity)
    return <li key={i} className={`tone-${tone}`}>
      <button type="button" className="remi-log-line" onClick={() => open({ kind: 'log', row })}>
        <time>{row.timestamp ? when(unix(row.timestamp)) : 'Unknown time'}</time>
        <span className={`chip tone-${tone}`}>{(parsed.severity ?? 'log').toLowerCase()}</span>
        <span className="remi-log-stream">{parsed.component ?? row.stream}</span>
        <span className="remi-log-text">{row.additional_to && <span className="chip tone-none remi-log-additional">additional to {row.additional_to}</span>}{typeof parsed.fields?.diagnostic_code === 'string' && <code>{parsed.fields.diagnostic_code} · </code>}{parsed.text}</span>
      </button>
    </li>
  })}</ol>
}

// --- Detail panel ----------------------------------------------------------------------------

interface AlarmChange { at: string; summary: string; from: string | null; to: string | null; reason: string }
interface TimelineEntry { at: number; until?: number; tone: Tone; title: string; text: string }

/** Alarm state changes, newest first, for every alarm given, looked up by its full name
 *  (a short name such as ScheduleDeliveryFailed can be REMI's or the agents'). */
function useAlarmHistory(environment: Environment, alarms: Alarm[]) {
  const [changes, setChanges] = useState<(AlarmChange & { alarm: string })[] | null>(null)
  const key = alarms.map(alarmId).join(',')
  useEffect(() => {
    let alive = true
    setChanges(null)
    Promise.all(alarms.map(alarm => api.get<{ changes: AlarmChange[] }>(`/eng/remi/alarms/${encodeURIComponent(alarmId(alarm))}/history`, { environment })
      .then(r => r.changes.map(c => ({ ...c, alarm: alarm.name }))).catch(() => [])))
      .then(all => { if (alive) setChanges(all.flat()) })
    return () => { alive = false }
  }, [environment, key]) // eslint-disable-line react-hooks/exhaustive-deps
  return changes
}

const changeTone = (c: AlarmChange): Tone => c.to === 'ALARM' ? 'bad' : c.to === 'OK' ? 'ok' : 'warn'
const stateWord = (s: string | null) => s === 'ALARM' ? 'firing' : s === 'OK' ? 'OK' : s === 'INSUFFICIENT_DATA' ? 'no data' : (s ?? '?')

function Timeline({ entries, loading, empty }: { entries: TimelineEntry[]; loading?: boolean; empty: string }) {
  const sorted = [...entries].sort((a, b) => b.at - a.at)
  return <div className="remi-timeline-wrap">
    {loading && <p className="muted tiny">Loading alarm history…</p>}
    {!loading && !sorted.length && <p className="empty">{empty}</p>}
    {!!sorted.length && <ol className="remi-timeline">{sorted.map((e, i) => <li key={i} className={`tone-${e.tone}`}>
      <time>{when(e.at)}{e.until && e.until !== e.at ? ` – ${time(e.until)}` : ''}</time>
      <strong>{e.title}</strong>
      {e.text && <p>{e.text}</p>}
    </li>)}</ol>}
  </div>
}

function Stats({ rows }: { rows: Spec['stats'] }) {
  const shown = rows.filter(r => r.points.length)
  if (!shown.length) return <p className="empty">No data in this window.</p>
  return <div className="table-wrap"><table className="remi-stats">
    <thead><tr><th>Series</th><th className="num">Latest</th><th className="num">Min</th><th className="num">Average</th><th className="num">Max</th></tr></thead>
    <tbody>{shown.map(r => {
      const values = r.points.map(([, v]) => v)
      return <tr key={r.label}><td>{r.label}</td><td className="num">{r.format(values[values.length - 1])}</td>
        <td className="num">{r.format(Math.min(...values))}</td><td className="num">{r.format(values.reduce((a, b) => a + b, 0) / values.length)}</td>
        <td className="num">{r.format(Math.max(...values))}</td></tr>
    })}</tbody>
  </table></div>
}

function TileDetail({ spec, page, window, onClose, open, range, loading }: {
  spec: Spec; page: Overview; window: Window; onClose: () => void; open: (d: Detail) => void; range: ReactNode; loading: boolean
}) {
  // A card's alarms are REMI's own, by their shown name.
  const related = page.alarms.filter(a => a.source !== 'agents' && spec.alarms.includes(a.name))
  const history = useAlarmHistory(page.environment, related)
  const logs = useLogQuery(page.environment, window)
  const entries: TimelineEntry[] = [
    ...(spec.notable ? episodes(spec.notable.points, spec.notable.test, page.period).map(e => ({
      at: e.from, until: e.to, tone: spec.notable!.tone, title: spec.notable!.label,
      text: `${secs(e.to - e.from + page.period)} · peak ${spec.notable!.format(e.peak)}`,
    })) : []),
    ...(history ?? []).map(c => ({ at: unix(c.at), tone: changeTone(c), title: `${c.alarm}: ${stateWord(c.from)} → ${stateWord(c.to)}`, text: c.reason })),
  ]
  return <SidePanel label={spec.title} onClose={onClose} head={<div>
    <span className="eyebrow">REMI · {page.environment} · {WINDOWS.find(([k]) => k === window)?.[1]}</span>
    <h2>{spec.title}</h2>
    {range}
    <div className="row job-panel-chips"><span className={`chip tone-${spec.tone}`}>{TONE_LABEL[spec.tone]}</span>
      <span className="chip tone-none">{headline(spec)}</span></div>
  </div>}>
    <div className="remi-detail" style={loading ? { visibility: 'hidden' } : undefined}>
      {(spec.cohorts || spec.percentiles || spec.secondary) && <section className="remi-detail-pairs">
        {spec.cohorts && <Cohorts values={spec.cohorts} />}
        {spec.percentiles && <Pair pair={{ ...spec.percentiles, label: spec.percentiles.label ?? spec.title }} />}
        {spec.secondary && <Pair pair={spec.secondary} />}
      </section>}
      <section><h3>What this measures</h3><p>{spec.definition}</p>
        <p className="remi-limits"><strong>Limits.</strong> {spec.limits}</p></section>
      <section><h3>Over time</h3><div className="remi-detail-chart">{spec.chart(false)}</div></section>
      <section><h3>Figures</h3><Stats rows={spec.stats} /></section>
      {spec.breakdown && <section><h3>{spec.breakdown.title}</h3><p className="muted tiny">{spec.breakdown.note}</p>
        {spec.breakdown.rows.length ? <div className="table-wrap"><table className="remi-stats remi-routes">
          <thead><tr>{spec.breakdown.columns.map((c, i) => <th key={c} className={i ? 'num' : undefined}>{c}</th>)}</tr></thead>
          <tbody>{spec.breakdown.rows.map(r => <tr key={r.label}><td className="remi-mono">{r.label}</td>
            {r.values.map((v, i) => <td key={i} className="num">{v}</td>)}</tr>)}</tbody>
        </table></div> : <p className="empty">{spec.breakdown.empty}</p>}</section>}
      <section><h3>Timeline</h3>
        <p className="muted tiny">Stretches outside the limits{related.length ? ', and state changes of the related alarms' : ''}, newest first.</p>
        <Timeline entries={entries} loading={related.length > 0 && history === null}
          empty="Nothing outside the limits in this window, and no alarm changed state." /></section>
      {!!related.length && <section><h3>Related alarms</h3><AlarmRows alarms={related} open={open} /></section>}
      {spec.logs && <section><h3>Related log lines</h3>
        <button type="button" className="ghost" disabled={logs.running} onClick={() => void logs.start(spec.logs!)}>Load related logs</button>
        {logs.running && <p className="muted tiny">Running…</p>}
        {logs.error && <div className="note err">{logs.error}</div>}
        {logs.result && !logs.result.rows.length && !logs.running && <p className="empty">No matching lines in this window.</p>}
        {!!logs.result?.rows.length && <LogLines rows={logs.result.rows.slice(0, 25)} open={open} />}
      </section>}
    </div>
  </SidePanel>
}

function AlarmDetail({ alarm, page, onClose }: { alarm: Alarm; page: LiveStatus; onClose: () => void }) {
  const history = useAlarmHistory(page.environment, [alarm])
  const tone = alarmTone(alarm)
  return <SidePanel label={`Alarm ${alarm.name}`} onClose={onClose} head={<div>
    <span className="eyebrow">Alarm · {alarm.id ?? alarm.name}</span>
    <h2>{alarm.name}</h2>
    <div className="row job-panel-chips"><span className={`chip tone-${tone}`}>{alarmLabel(alarm)}</span>
      <span className="chip tone-none">since {when(unix(alarm.since))}</span></div>
  </div>}>
    <div className="remi-detail">
      <section><h3>What it watches</h3><p>{alarm.description || 'No description.'}</p>
        {(alarm.component || alarm.severity) && <p className="muted small">{[alarm.component, alarm.severity].filter(Boolean).join(' · ')}</p>}
        {alarm.recommendation && <p><strong>First check.</strong> {alarm.recommendation}</p>}
        {alarm.reason && <p className="remi-reason"><strong>Last evaluation.</strong> {alarm.reason}</p>}</section>
      <section><h3>Timeline</h3><p className="muted tiny">State changes, newest first.</p>
        <Timeline loading={history === null} empty="No state changes recorded."
          entries={(history ?? []).map(c => ({ at: unix(c.at), tone: changeTone(c), title: `${stateWord(c.from)} → ${stateWord(c.to)}`, text: c.reason }))} /></section>
    </div>
  </SidePanel>
}

function LogDetail({ row, onClose }: { row: LogRow; onClose: () => void }) {
  const parsed = useMemo(() => parseLog(row), [row])
  const tone = logTone(parsed.severity)
  const fields = parsed.fields ? Object.entries(parsed.fields).filter(([k]) => k !== 'message') : []
  return <SidePanel label="Log line" onClose={onClose} head={<div>
    <span className="eyebrow">Log line · {row.stream}</span>
    <h2 className="remi-log-title">{parsed.text}</h2>
    <div className="row job-panel-chips"><span className={`chip tone-${tone}`}>{(parsed.severity ?? 'log').toLowerCase()}</span>
      <span className="chip tone-none">{row.timestamp ? when(unix(row.timestamp)) : 'Unknown time'}</span>
      {row.additional_to && <span className="chip tone-none">additional to {row.additional_to}</span>}</div>
    {row.additional_to && <p className="muted small">Recorded after the attempt's own failure, which stays the cause; this is not a separate failure.</p>}
  </div>}>
    <div className="remi-detail">
      {!!fields.length && <section><h3>Fields</h3><div className="table-wrap"><table className="remi-stats"><tbody>
        {fields.map(([k, v]) => <tr key={k}><th>{k}</th><td className="remi-mono">{typeof v === 'string' ? v : JSON.stringify(v)}</td></tr>)}
      </tbody></table></div></section>}
      <section><h3>Raw line</h3><pre className="remi-raw">{row.message}</pre></section>
    </div>
  </SidePanel>
}
